import { type Theme, alpha } from "@mui/material";

const unifiedHeaderBg = (theme: Theme) =>
	theme.palette.mode === "light"
		? theme.palette.grey[50]
		: alpha(theme.palette.common.white, 0.03);

const unifiedStripeBg = (theme: Theme) =>
	theme.palette.mode === "light"
		? alpha(theme.palette.common.black, 0.015)
		: alpha(theme.palette.common.white, 0.02);

const unifiedHoverBg = (theme: Theme) =>
	theme.palette.mode === "light"
		? alpha(theme.palette.common.black, 0.03)
		: alpha(theme.palette.common.white, 0.045);

export const sharedGridScrollbarSx = (theme: Theme) => ({
	"&::-webkit-scrollbar": { width: 6, height: 6 },
	"&::-webkit-scrollbar-track": { background: "transparent" },
	"&::-webkit-scrollbar-thumb": {
		borderRadius: 3,
		background:
			theme.palette.mode === "dark"
				? "rgba(255,255,255,0.2)"
				: "rgba(0,0,0,0.15)",
	},
	"&::-webkit-scrollbar-thumb:hover": {
		background:
			theme.palette.mode === "dark"
				? "rgba(255,255,255,0.3)"
				: "rgba(0,0,0,0.25)",
	},
});

export const sharedTableTagChipSx = (theme: Theme) => ({
	height: 22,
	borderRadius: 999,
	fontWeight: 700,
	border: "1px solid",
	borderColor: alpha(
		theme.palette.primary.main,
		theme.palette.mode === "dark" ? 0.28 : 0.18,
	),
	backgroundColor:
		theme.palette.mode === "dark"
			? alpha(theme.palette.primary.main, 0.18)
			: alpha(theme.palette.primary.main, 0.08),
	color:
		theme.palette.mode === "dark"
			? theme.palette.primary.light
			: theme.palette.primary.dark,
	"& .MuiChip-label": {
		px: 1.1,
		display: "block",
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
	maxWidth: 160,
});

export const sharedTableCommentSummaryChipSx = (theme: Theme) => ({
	height: 22,
	borderRadius: 999,
	fontWeight: 700,
	border: "1px solid",
	borderColor: alpha(
		theme.palette.info.main,
		theme.palette.mode === "dark" ? 0.3 : 0.18,
	),
	backgroundColor:
		theme.palette.mode === "dark"
			? alpha(theme.palette.info.main, 0.16)
			: alpha(theme.palette.info.main, 0.08),
	color:
		theme.palette.mode === "dark"
			? theme.palette.info.light
			: theme.palette.info.dark,
	"& .MuiChip-label": {
		px: 1.1,
	},
});

export const sharedTableCheckboxSx = (theme: Theme) => ({
	p: 0.25,
	color: alpha(
		theme.palette.text.secondary,
		theme.palette.mode === "dark" ? 0.45 : 0.35,
	),
	"& .MuiSvgIcon-root": {
		fontSize: 18,
	},
	"&.Mui-checked": {
		color:
			theme.palette.mode === "dark"
				? theme.palette.primary.light
				: theme.palette.primary.main,
	},
	"&.Mui-disabled": {
		color: alpha(
			theme.palette.text.secondary,
			theme.palette.mode === "dark" ? 0.42 : 0.32,
		),
	},
	"&.Mui-checked.Mui-disabled": {
		color:
			theme.palette.mode === "dark"
				? alpha(theme.palette.primary.light, 0.95)
				: alpha(theme.palette.primary.main, 0.9),
	},
});

export const sharedUnifiedDataGridVisualsSx = (theme: Theme) => ({
	"& .MuiDataGrid-root": {
		border: "none",
		backgroundColor: theme.palette.background.paper,
	},
	"& .MuiDataGrid-columnHeaders": {
		backgroundColor: unifiedHeaderBg(theme),
		color: theme.palette.text.primary,
		fontSize: "0.8125rem",
		fontWeight: 700,
		borderBottom: `1px solid ${theme.palette.divider}`,
		boxShadow: `inset 0 -1px 0 ${theme.palette.divider}`,
	},
	"& .MuiDataGrid-columnHeader": {
		borderColor: alpha(theme.palette.text.primary, 0.08),
		px: 1.25,
	},
	"& .MuiDataGrid-columnHeaderTitle": {
		fontWeight: 700,
		letterSpacing: 0,
	},
	"& .MuiDataGrid-columnSeparator": {
		color: alpha(theme.palette.text.secondary, 0.12),
	},
	"& .MuiDataGrid-row.row-even": {
		backgroundColor: theme.palette.background.paper,
	},
	"& .MuiDataGrid-row.row-odd": {
		backgroundColor: unifiedStripeBg(theme),
	},
	"& .MuiDataGrid-row:hover": {
		backgroundColor: unifiedHoverBg(theme),
		cursor: "pointer",
	},
	"& .MuiDataGrid-row": {
		borderBottom: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
	},
	"& .MuiDataGrid-cell": {
		borderColor: alpha(theme.palette.text.primary, 0.06),
		borderBottom: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
		padding: "10px 12px",
		lineHeight: 1.4,
		whiteSpace: "normal",
		wordWrap: "break-word",
		alignItems: "center",
	},
	"& .MuiDataGrid-cellContent": {
		overflow: "visible !important",
		textOverflow: "unset",
	},
	"& .MuiDataGrid-columnHeadersInner, & .MuiDataGrid-columnHeaderDraggableContainer":
		{
			alignItems: "center",
		},
	"& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
		outline: "none",
	},
	"& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
		{
			outline: "none",
		},
	"& .MuiDataGrid-footerContainer": {
		borderTop: `1px solid ${theme.palette.divider}`,
		backgroundColor: unifiedHeaderBg(theme),
		minHeight: 48,
	},
	"& .MuiDataGrid-toolbarContainer": {
		p: 0,
		gap: 0,
		flexShrink: 0,
	},
	"& .MuiTablePagination-root": {
		color: theme.palette.text.secondary,
	},
	"& .MuiDataGrid-overlayWrapper, & .MuiDataGrid-overlayWrapperInner": {
		backgroundColor: theme.palette.background.paper,
	},
});

export const sharedShiftDataGridShellSx = (
	theme: Theme,
	options?: { includeInnerScrollbar?: boolean },
) => ({
	flex: 1,
	minHeight: 0,
	borderRadius: 2,
	overflow: "hidden",
	display: "flex",
	flexDirection: "column",
	...sharedUnifiedDataGridVisualsSx(theme),
	"& .MuiDataGrid-main": {
		overflow: "hidden",
	},
	"& .MuiDataGrid-virtualScroller": {
		overflow: "auto",
		overscrollBehavior: "contain",
		WebkitOverflowScrolling: "touch",
		...(options?.includeInnerScrollbar ? sharedGridScrollbarSx(theme) : {}),
	},
});

export const sharedAutoHeightDataGridSectionSx = (theme: Theme) => ({
	position: "relative",
	flex: 1,
	height: 0,
	minHeight: 0,
	overflow: "auto",
	WebkitOverflowScrolling: "touch",
	...sharedGridScrollbarSx(theme),
	...sharedUnifiedDataGridVisualsSx(theme),
	"& .MuiDataGrid-main": {
		overflow: "visible",
	},
	"& .MuiDataGrid-virtualScroller": {
		overflow: "visible !important",
	},
});
